import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * VocabCard — shows an AI/ML term in a compact tile.
 * Click to expand the full definition and related terms.
 *
 * Props:
 *   term: { id, term, shortDef, definition, category, categoryLabel, categoryColor, related }
 */
export default function VocabCard({ term }) {
  const [expanded, setExpanded] = useState(false);

  const {
    term: label,
    shortDef,
    definition,
    categoryLabel,
    categoryColor,
    related = [],
  } = term;

  return (
    <motion.article
      className="vocab-card"
      layout
      onClick={() => setExpanded(!expanded)}
      style={{ cursor: "pointer" }}
      whileHover={{ y: -2 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
    >
      <div className="vocab-card__header">
        <div>
          <h3 className="vocab-card__term">{label}</h3>
          <p className="vocab-card__short">{shortDef}</p>
        </div>
        <div className="vocab-card__right">
          <span
            className="vocab-card__category"
            style={{ "--cat-color": categoryColor }}
          >
            {categoryLabel}
          </span>
          <span className="vocab-card__toggle">{expanded ? "−" : "+"}</span>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            className="vocab-card__body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <hr className="vocab-card__divider" />
            <p className="vocab-card__definition">{definition}</p>
            {related.length > 0 && (
              <div className="vocab-card__related">
                <span className="vocab-card__related-label">Related: </span>
                {related.join(" · ")}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .vocab-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: var(--space-md);
          transition: border-color var(--transition-base);
          user-select: none;
        }
        .vocab-card:hover { border-color: var(--accent); }
        .vocab-card__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: var(--space-sm);
        }
        .vocab-card__term {
          font-size: 1rem;
          font-family: var(--font-body);
          font-weight: 600;
          color: var(--text);
          margin-bottom: 0.2rem;
        }
        .vocab-card__short {
          font-size: 0.82rem;
          color: var(--text-muted);
          line-height: 1.45;
        }
        .vocab-card__right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: var(--space-xs);
          flex-shrink: 0;
        }
        .vocab-card__category {
          font-size: 0.7rem;
          font-weight: 500;
          padding: 0.15rem 0.6rem;
          border-radius: var(--radius-full);
          background: color-mix(in srgb, var(--cat-color) 15%, transparent);
          color: var(--cat-color);
          border: 1px solid color-mix(in srgb, var(--cat-color) 25%, transparent);
        }
        .vocab-card__toggle {
          color: var(--text-muted);
          font-size: 1.2rem;
          line-height: 1;
          font-weight: 300;
        }
        .vocab-card__divider {
          border: none;
          border-top: 1px solid var(--border);
          margin: var(--space-md) 0;
        }
        .vocab-card__definition {
          font-size: 0.88rem;
          color: var(--text);
          line-height: 1.6;
          margin-bottom: var(--space-sm);
        }
        .vocab-card__related {
          font-size: 0.78rem;
          color: var(--text-muted);
        }
        .vocab-card__related-label { color: var(--accent); font-weight: 500; }
        /* Collapse animation overflow clip */
        .vocab-card__body { overflow: hidden; }
      `}</style>
    </motion.article>
  );
}
